import { Avatar, AvatarFallback, AvatarImage } from '@radix-ui/react-avatar'
import {
  Building,
  Check,
  ChevronDown,
  Globe,
  HelpCircle,
  LogOut,
  Menu,
  Settings,
  Store,
  User,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'

import { startProductTour } from '@/components/shared/ProductTour'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { useUploadPreSignedUrl, useUploads } from '@/hooks'
import { useAuth } from '@/hooks/useAuth'
import { changeLanguage, languages } from '@/i18n'
import { APP_ROUTES } from '@/libs/constants'
import type { Organization } from '@/libs/interfaces'
import { cn } from '@/libs/utils'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { setCurrentStore } from '@/store/slices/authSlice'

import { FullPageLoader } from '../shared'

interface RouteInfo {
  title: string
  subtitle?: string
}

interface TopbarProps {
  onOpenMobileSidebar?: () => void
}

export function AppTopbar({ onOpenMobileSidebar }: TopbarProps) {
  const location = useLocation()
  const { t, i18n } = useTranslation()
  const { user, currentOrganization, currentStore } = useAppSelector((state) => state.auth)
  const { logout, switchOrganization, isLoggingOut } = useAuth()
  const dispatch = useAppDispatch()

  const orgID: string = useAppSelector((state) => state?.auth?.currentOrganization?.orgID) ?? ''

  const organizations = JSON.parse(localStorage.getItem('organizations') || '[]') as Organization[]
  const currentLanguage = languages.find((lang) => lang.code === i18n.language) || languages[0]

  const userID = user?.userID ?? ''

  const { data: uploadsData } = useUploads({
    params: { orgID, userID, profilePic: true, limit: 1 },
    enabled: !!orgID && !!userID,
  })
  const profileUpload = uploadsData?.uploads?.[0]

  const { data: profilePicUrl } = useUploadPreSignedUrl({
    params: { orgID, uploadID: profileUpload?.uploadID ?? '' },
    enabled: !!profileUpload?.uploadID,
  })

  const routeMap: Record<string, RouteInfo> = {
    [APP_ROUTES.home]: { title: t('nav.dashboard'), subtitle: t('topbar.dashboardSubtitle') },
    // [APP_ROUTES.boo]: { title: t('nav.scheduler'), subtitle: t('topbar.schedulerSubtitle') },
    [APP_ROUTES.booking.base]: { title: t('nav.booking'), subtitle: t('topbar.bookingSubtitle') },
    [APP_ROUTES.booking.new()]: {
      title: t('booking.newBooking'),
      subtitle: t('topbar.newBookingSubtitle'),
    },
    [APP_ROUTES.customer.base]: {
      title: t('nav.customers'),
      subtitle: t('topbar.customersSubtitle'),
    },
    [APP_ROUTES.customer.new]: {
      title: t('customers.addCustomer'),
      subtitle: t('topbar.newCustomerSubtitle'),
    },
    // [APP_ROUTES.customer.newCompany]: {
    //   title: t('customers.addCompany'),
    //   subtitle: t('topbar.newCompanySubtitle'),
    // },
    [APP_ROUTES.vehicle.base]: { title: t('nav.vehicles'), subtitle: t('topbar.vehiclesSubtitle') },
    [APP_ROUTES.vehicle.new]: {
      title: t('vehicles.addVehicle'),
      subtitle: t('topbar.newVehicleSubtitle'),
    },
    [APP_ROUTES.settings.service.base]: {
      title: t('nav.services'),
      subtitle: t('topbar.servicesSubtitle'),
    },
    [APP_ROUTES.settings.service.new]: {
      title: t('services.addService'),
      subtitle: t('topbar.newServiceSubtitle'),
    },
    [APP_ROUTES.job.base]: { title: t('nav.jobs'), subtitle: t('topbar.jobsSubtitle') },
    [APP_ROUTES.pricing]: { title: t('nav.pricing'), subtitle: t('topbar.pricingSubtitle') },
    [APP_ROUTES.roles]: { title: t('nav.roles'), subtitle: t('topbar.rolesSubtitle') },
    [APP_ROUTES.settings.base]: {
      title: t('nav.settings'),
      subtitle: t('topbar.settingsSubtitle'),
    },
    [APP_ROUTES.account]: { title: t('nav.account'), subtitle: t('topbar.accountSubtitle') },
    [APP_ROUTES.changePassword]: { title: t('nav.changePassword') },
    [APP_ROUTES.leaves]: { title: t('nav.leaves'), subtitle: t('topbar.leavesSubtitle') },
    [APP_ROUTES.workLogs]: { title: t('nav.workLogs'), subtitle: t('topbar.workLogsSubtitle') },
    // [APP_ROUTES.invoicing.base]: { title: t('nav.billing'), subtitle: t('topbar.billingSubtitle') },
    [APP_ROUTES.product.base]: { title: t('nav.products'), subtitle: t('topbar.productsSubtitle') },
    [APP_ROUTES.product.new]: {
      title: t('products.addProduct'),
      subtitle: t('topbar.newProductSubtitle'),
    },
    [APP_ROUTES.rentCar.base]: {
      title: t('nav.rentalFleet'),
      subtitle: t('topbar.rentalFleetSubtitle'),
    },
    [APP_ROUTES.rentCar.new]: {
      title: t('rentalFleet.addVehicle'),
      subtitle: t('rentalFleet.addVehicleDesc'),
    },
    [APP_ROUTES.campaigns]: { title: t('nav.campaigns'), subtitle: t('topbar.campaignsSubtitle') },
    [APP_ROUTES.integrations]: {
      title: t('nav.integrations'),
      subtitle: t('topbar.integrationsSubtitle'),
    },
  }

  const getPageInfo = (): RouteInfo => {
    if (routeMap[location.pathname]) {
      return routeMap[location.pathname]
    }
    const pathSegments = location.pathname.split('/').filter(Boolean)
    while (pathSegments.length > 0) {
      const parentPath = '/' + pathSegments.join('/')
      if (routeMap[parentPath]) {
        return routeMap[parentPath]
      }
      pathSegments.pop()
    }
    return { title: t('nav.dashboard'), subtitle: t('topbar.dashboardSubtitle') }
  }

  const pageInfo = getPageInfo()

  return (
    <TooltipProvider delayDuration={300}>
      {isLoggingOut && <FullPageLoader />}
      <header className="h-14 md:h-16 bg-card border-b border-border shadow-sm flex items-center justify-between pr-3 md:px-5 md:sticky md:top-0 md:z-50 shrink-0">
        {/* Page Title with subtitle */}
        <div className="flex items-center gap-2 min-w-0" data-tour="topbar-title">
          <button
            onClick={onOpenMobileSidebar}
            className="md:hidden h-14 w-12 shrink-0 inline-flex items-center justify-center border-r border-border text-foreground transition-colors hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            aria-label={t('nav.expandSidebar')}
          >
            <Menu className="w-5 h-5" />
          </button>
          <div className="min-w-0">
            <h1 className="text-sm md:text-base font-semibold text-foreground truncate tracking-tight">
              {pageInfo.title}
            </h1>
            {pageInfo.subtitle && (
              <p className="text-[11px] md:text-xs text-muted-foreground truncate -mt-0.5">
                {pageInfo.subtitle}
              </p>
            )}
          </div>
        </div>

        {/* Topbar Actions */}
        <div className="flex items-center gap-3">
          {/* Store Selector */}
          <Tooltip>
            <TooltipTrigger asChild>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    className="flex items-center gap-2 px-3 h-9"
                    data-tour="topbar-store"
                  >
                    <Store className="w-4 h-4" />
                    <span className="hidden sm:inline max-w-[120px] truncate">
                      {currentStore?.storeName || t('store.mainStore')}
                    </span>
                    <ChevronDown className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>{t('store.stores')}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {currentOrganization?.stores && currentOrganization.stores.length > 0 ? (
                    currentOrganization.stores.map((store) => {
                      const isSelected = currentStore?.storeID === store.storeID
                      return (
                        <DropdownMenuItem
                          key={store.storeID}
                          className={cn(
                            'flex items-center justify-between cursor-pointer',
                            isSelected && 'bg-secondary',
                          )}
                          onClick={() => dispatch(setCurrentStore(store))}
                        >
                          <div className="flex flex-col">
                            <span className="font-medium">{store.storeName}</span>
                            <span className="text-caption">{store.storeAddress}</span>
                          </div>
                          {isSelected && <Check className="w-4 h-4 text-primary" />}
                        </DropdownMenuItem>
                      )
                    })
                  ) : (
                    <DropdownMenuItem disabled>
                      <span className="text-muted-foreground">{t('store.noStores')}</span>
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </TooltipTrigger>
            <TooltipContent>{t('tooltips.topbar.store')}</TooltipContent>
          </Tooltip>

          {/* How To Guide */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={startProductTour}
                data-tour="topbar-howto"
                aria-label={t('common.help')}
              >
                <HelpCircle className="w-5 h-5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t('topbar.howTo')}</TooltipContent>
          </Tooltip>

          {/* User Menu */}
          <Tooltip>
            <TooltipTrigger asChild>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="flex items-center gap-2 px-2"
                    data-tour="topbar-user"
                  >
                    <Avatar className="w-8 h-8">
                      <AvatarImage src={profilePicUrl} />
                      <AvatarFallback
                        className={cn(
                          'w-8 h-8 rounded-full flex items-center justify-center',
                          'bg-primary text-primary-foreground font-semibold text-sm',
                        )}
                      >
                        {user?.firstName
                          ?.split(' ')
                          .map((n) => n[0])
                          .join('') || 'JD'}
                      </AvatarFallback>
                    </Avatar>
                    <div className="hidden md:flex flex-col items-start">
                      <span className="label-sm">{user?.firstName}</span>
                      <span className="text-caption">{organizations?.[0]?.roleName}</span>
                    </div>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>{t('user.myAccount')}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to={APP_ROUTES.account} className="flex items-center cursor-pointer">
                      <User className="mr-2 h-4 w-4" />
                      {t('user.profile')}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link
                      to={APP_ROUTES.settings.base}
                      className="flex items-center cursor-pointer"
                    >
                      <Settings className="mr-2 h-4 w-4" />
                      {t('user.settings')}
                    </Link>
                  </DropdownMenuItem>
                  {/* Organization Switcher */}
                  {organizations.length > 0 && (
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger>
                        <Building className="mr-2 h-4 w-4" />
                        {t('org.organization')}
                        <span className="ml-auto text-xs text-muted-foreground truncate max-w-[80px]">
                          {currentOrganization?.orgName || organizations[0]?.orgName}
                        </span>
                      </DropdownMenuSubTrigger>
                      <DropdownMenuSubContent
                        sideOffset={8}
                        alignOffset={-5}
                        className="min-w-[200px]"
                      >
                        {organizations.map((org) => (
                          <DropdownMenuItem
                            key={org.orgID}
                            onClick={() => switchOrganization(org)}
                            className={cn(
                              'flex items-center justify-between cursor-pointer',
                              currentOrganization?.orgID === org.orgID && 'bg-secondary',
                            )}
                          >
                            <div className="flex flex-col">
                              <span className="font-medium">{org.orgName}</span>
                              <span className="text-xs text-muted-foreground">{org.roleName}</span>
                            </div>
                            {currentOrganization?.orgID === org.orgID && (
                              <Check className="w-4 h-4 text-primary" />
                            )}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>
                  )}
                  {/* Language Switcher */}
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger>
                      <Globe className="mr-2 h-4 w-4" />
                      {t('user.language')}
                      <span className="ml-auto text-xs text-muted-foreground">
                        {currentLanguage.flag}
                      </span>
                    </DropdownMenuSubTrigger>
                    <DropdownMenuSubContent sideOffset={8} alignOffset={-5}>
                      {languages.map((language) => (
                        <DropdownMenuItem
                          key={language.code}
                          onClick={() => changeLanguage(language.code)}
                          className={language.code === currentLanguage.code ? 'bg-secondary' : ''}
                        >
                          <span className="mr-2">{language.flag}</span>
                          {language.name}
                          {language.code === currentLanguage.code && (
                            <Check className="ml-auto h-4 w-4" />
                          )}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuSubContent>
                  </DropdownMenuSub>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} className="text-destructive cursor-pointer">
                    <LogOut className="mr-2 h-4 w-4" />
                    {t('auth.logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </TooltipTrigger>
            <TooltipContent>{t('tooltips.topbar.userMenu')}</TooltipContent>
          </Tooltip>
        </div>
      </header>
    </TooltipProvider>
  )
}
